/**
 * Wrappers for `/api/builder/{draft_key}/training` (Pydantic schemas in
 * `src/builder/schemas/training.py`).
 *
 * Three flavours of training row distinguished by `training_type`:
 *  - `ddl` — raw `CREATE TABLE …` (typically one row per draft).
 *  - `documentation` — markdown blob (one row per old `.md` file).
 *  - `example` — few-shot Q&A; `content` is the SQL,
 *    `metadata.question` is the natural-language prompt,
 *    `metadata.explanation` (optional) the rationale.
 */

import { api } from "@/lib/api";

export type TrainingType = "ddl" | "documentation" | "example";

export interface TrainingItem {
  id: string;
  report_draft_id: string;
  training_type: TrainingType;
  name: string;
  content: string;
  metadata: Record<string, unknown>;
  position: number;
  created_at: string;
  updated_at: string;
}

export interface TrainingItemCreate {
  training_type: TrainingType;
  name: string;
  content?: string;
  metadata?: Record<string, unknown>;
  position?: number;
}

export interface TrainingItemUpdate {
  name?: string;
  content?: string;
  metadata?: Record<string, unknown>;
  position?: number;
}

export interface TrainingListResponse {
  items: TrainingItem[];
  total: number;
}

export interface TrainingBulkReplaceItem {
  id?: string | null;
  training_type: TrainingType;
  name: string;
  content?: string;
  metadata?: Record<string, unknown>;
  position?: number;
}

export interface TrainingBulkReplacePayload {
  items: TrainingBulkReplaceItem[];
  /** When set, only rows of this type are reconciled. */
  training_type_filter?: TrainingType | null;
}

const base = (draftKey: string) =>
  `/api/builder/${encodeURIComponent(draftKey)}/training`;

export const trainingApi = {
  list: (draftKey: string, type?: TrainingType) =>
    api.get<TrainingListResponse>(base(draftKey), {
      query: { training_type: type },
    }),
  create: (draftKey: string, payload: TrainingItemCreate) =>
    api.post<TrainingItem>(base(draftKey), payload),
  update: (draftKey: string, id: string, payload: TrainingItemUpdate) =>
    api.put<TrainingItem>(`${base(draftKey)}/${id}`, payload),
  remove: (draftKey: string, id: string) =>
    api.delete<void>(`${base(draftKey)}/${id}`),
  bulkReplace: (draftKey: string, payload: TrainingBulkReplacePayload) =>
    api.post<TrainingListResponse>(`${base(draftKey)}/bulk`, payload),
};
